import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const AboutUs = () => {

  // Schema injection via useEffect
  useEffect(() => {
    const aboutSchema = {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "About SERPfinity",
      "description": "Learn about SERPfinity, the AI-powered search visibility platform helping businesses master SEO and AEO.",
      "url": "https://app.serpfinity.ai/about",
      "mainEntity": {
        "@type": "Organization",
        "name": "SERPfinity",
        "url": "https://app.serpfinity.ai",
        "logo": "https://app.serpfinity.ai/images/serpfinity-logo.png",
        "description": "SERPfinity is an AI-powered search visibility platform that helps businesses optimize for both traditional search engines and AI-powered answer engines.",
        "contactPoint": {
          "@type": "ContactPoint",
          "email": "info@serpfinity.ai",
          "contactType": "customer support"
        }
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(aboutSchema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>About SERPfinity - Our Mission to Revolutionize AI Search Visibility</title>
        <meta name="description" content="Learn about SERPfinity's mission to help businesses dominate AI-powered search. Discover our story, values, and the team behind the leading SEO and AEO platform." />
        <meta name="keywords" content="about SERPfinity, AI search visibility company, SEO platform, AEO tools, search optimization" />
        <meta property="og:title" content="About SERPfinity - Our Mission to Revolutionize AI Search Visibility" />
        <meta property="og:description" content="Learn about SERPfinity's mission to help businesses dominate AI-powered search. Discover our story, values, and the team behind the leading SEO and AEO platform." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://app.serpfinity.ai/about" />
        <meta property="og:image" content="https://app.serpfinity.ai/og-image.png" />
        <meta property="og:site_name" content="SERPfinity" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About SERPfinity - Our Mission to Revolutionize AI Search Visibility" />
        <meta name="twitter:description" content="Learn about SERPfinity's mission to help businesses dominate AI-powered search. Discover our story, values, and the team behind the leading SEO and AEO platform." />
        <meta name="twitter:image" content="https://app.serpfinity.ai/og-image.png" />
        <link rel="canonical" href="https://app.serpfinity.ai/about" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">About SERPfinity</h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto">
            Empowering businesses to master search visibility in the age of AI. We build the tools you need to get found by both search engines and AI assistants.
          </p>
        </div>
      </section>

      {/* Our Mission */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Our Mission</h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-6">
            At SERPfinity, we believe every business deserves to be visible where their customers are searching. As AI transforms how people find information online, traditional SEO alone is no longer enough. That's why we built a platform that bridges the gap between classic search engine optimization and the new world of Answer Engine Optimization (AEO).
          </p>
          <p className="text-lg text-gray-600 leading-relaxed">
            Our mission is simple: help businesses of all sizes track, measure, and improve their visibility across every search platform  from Google and Bing to ChatGPT, Perplexity, Google AI Overviews, and beyond.
          </p>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">What We Do</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">AI Search Visibility Tracking</h3>
              <p className="text-gray-600">Monitor how your brand appears across AI-powered search platforms including ChatGPT, Google AI Overviews, Perplexity, and Bing Copilot.</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Comprehensive SEO Tools</h3>
              <p className="text-gray-600">From rank tracking and SERP analysis to keyword research, backlink monitoring, site audits, and content optimization  everything in one platform.</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">AI Content Writer</h3>
              <p className="text-gray-600">Create SEO-optimized and AEO-ready content with our AI-powered writing assistant that helps you rank in both traditional and AI search results.</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Local SEO</h3>
              <p className="text-gray-600">Manage your local search presence with listing management, local rank tracking, and location-based visibility insights.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why SERPfinity */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Why SERPfinity?</h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-6">
            The search landscape is changing faster than ever. AI chatbots and answer engines are reshaping how users discover information, products, and services. Businesses that adapt early will have a massive competitive advantage.
          </p>
          <p className="text-lg text-gray-600 leading-relaxed mb-6">
            SERPfinity was built from the ground up to address this shift. While other tools focus solely on traditional SEO metrics, we combine classic search optimization with cutting-edge AI visibility tracking  giving you a complete picture of your online presence.
          </p>
          <p className="text-lg text-gray-600 leading-relaxed">
            Whether you're a small business owner, an SEO professional, or a marketing agency, SERPfinity gives you the insights and tools to stay ahead of the curve.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join thousands of businesses already using SERPfinity to dominate search visibility. Start your free trial today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-indigo-600 bg-white hover:bg-gray-50 transition-colors">
              Start Free Trial
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-3 border-2 border-white text-base font-medium rounded-md text-white hover:bg-white/10 transition-colors">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
